// Variable Resolver Utility
// Resolves variable references (var(--variable-name)) to actual values

/**
 * Resolve variable references in a style object
 * @param {Object} styles - Style object with potential variable references
 * @param {Object} globalVariables - Global variables object with variables array
 * @returns {Object} Resolved styles with actual values
 */
export const resolveVariables = (styles, globalVariables) => {
    if (!styles || !globalVariables?.variables) return styles;

    const variableMap = {};
    globalVariables.variables.forEach(v => {
        variableMap[v.name] = v.value;
    });

    const resolved = {};
    Object.entries(styles).forEach(([key, value]) => {
        if (typeof value === 'string' && value.startsWith('var(--')) {
            const varName = value.match(/var\((--[^)]+)\)/)?.[1];
            resolved[key] = variableMap[varName] || value;
        } else {
            resolved[key] = value;
        }
    });

    return resolved;
};

/**
 * Check if a value is a variable reference
 * @param {string} value - Value to check
 * @returns {boolean}
 */
export const isVariableReference = (value) => {
    return typeof value === 'string' && value.startsWith('var(--') && value.endsWith(')');
};

/**
 * Extract variable name from reference
 * @param {string} reference - Variable reference like "var(--primary-color)"
 * @returns {string} Variable name like "--primary-color"
 */
export const extractVariableName = (reference) => {
    const match = reference.match(/var\((--[^)]+)\)/);
    return match ? match[1] : null;
};

/**
 * Get default variables for a new template
 * COMPREHENSIVE color palette matching actual widget colors
 * @returns {Object} Default variables object
 */
export const getDefaultVariables = () => {
    return {
        variables: [
            // ========== PRIMARY & SECONDARY COLORS ==========
            {
                id: 'var-1',
                name: '--primary-color',
                type: 'color',
                value: '#007bff',
                description: 'Primary brand color',
                category: 'colors'
            },
            {
                id: 'var-2',
                name: '--secondary-color',
                type: 'color',
                value: '#6c757d',
                description: 'Secondary color',
                category: 'colors'
            },

            // ========== BASE COLORS ==========
            {
                id: 'var-3',
                name: '--text-color',
                type: 'color',
                value: '#212529',
                description: 'Main text color',
                category: 'colors'
            },
            {
                id: 'var-4',
                name: '--background-color',
                type: 'color',
                value: '#ffffff',
                description: 'Background color',
                category: 'colors'
            },
            {
                id: 'var-5',
                name: '--border-color',
                type: 'color',
                value: '#dee2e6',
                description: 'Border color',
                category: 'colors'
            },

            // ========== SUCCESS COLORS (GREEN SHADES) ==========
            {
                id: 'var-6',
                name: '--success-color',
                type: 'color',
                value: '#28a745',
                description: 'Success primary color',
                category: 'colors'
            },
            {
                id: 'var-7',
                name: '--success-bg',
                type: 'color',
                value: '#d1fae5',
                description: 'Success background (light green)',
                category: 'colors'
            },
            {
                id: 'var-8',
                name: '--success-text',
                type: 'color',
                value: '#065f46',
                description: 'Success text (dark green)',
                category: 'colors'
            },
            {
                id: 'var-9',
                name: '--success-progress',
                type: 'color',
                value: '#6fca42',
                description: 'Success progress bar',
                category: 'colors'
            },
            {
                id: 'var-10',
                name: '--success-border',
                type: 'color',
                value: '#6fca42',
                description: 'Success border color',
                category: 'colors'
            },

            // ========== WARNING COLORS (YELLOW/ORANGE SHADES) ==========
            {
                id: 'var-11',
                name: '--warning-color',
                type: 'color',
                value: '#ffc107',
                description: 'Warning primary color',
                category: 'colors'
            },
            {
                id: 'var-12',
                name: '--warning-bg',
                type: 'color',
                value: '#fef3c7',
                description: 'Warning background (light yellow)',
                category: 'colors'
            },
            {
                id: 'var-13',
                name: '--warning-text',
                type: 'color',
                value: '#92400e',
                description: 'Warning text (dark yellow)',
                category: 'colors'
            },
            {
                id: 'var-14',
                name: '--warning-border',
                type: 'color',
                value: '#f59e0b',
                description: 'Warning border color',
                category: 'colors'
            },

            // ========== ERROR COLORS (RED SHADES) ==========
            {
                id: 'var-15',
                name: '--error-color',
                type: 'color',
                value: '#dc3545',
                description: 'Error primary color',
                category: 'colors'
            },
            {
                id: 'var-16',
                name: '--error-bg',
                type: 'color',
                value: '#fee2e2',
                description: 'Error background (light red)',
                category: 'colors'
            },
            {
                id: 'var-17',
                name: '--error-text',
                type: 'color',
                value: '#991b1b',
                description: 'Error text (dark red)',
                category: 'colors'
            },
            {
                id: 'var-18',
                name: '--error-border',
                type: 'color',
                value: '#ef4444',
                description: 'Error border color',
                category: 'colors'
            },

            // ========== INFO/ATTENTION COLORS (BLUE SHADES) ==========
            {
                id: 'var-19',
                name: '--info-color',
                type: 'color',
                value: '#17a2b8',
                description: 'Info primary color',
                category: 'colors'
            },
            {
                id: 'var-20',
                name: '--info-bg',
                type: 'color',
                value: '#dbeafe',
                description: 'Info background (light blue)',
                category: 'colors'
            },
            {
                id: 'var-21',
                name: '--info-text',
                type: 'color',
                value: '#1e40af',
                description: 'Info text (dark blue)',
                category: 'colors'
            },

            // ========== FONTS ==========
            {
                id: 'var-22',
                name: '--heading-font',
                type: 'font',
                value: 'Arial, sans-serif',
                description: 'Font for headings',
                category: 'fonts'
            },
            {
                id: 'var-23',
                name: '--body-font',
                type: 'font',
                value: 'Arial, sans-serif',
                description: 'Font for body text',
                category: 'fonts'
            },
            {
                id: 'var-24',
                name: '--monospace-font',
                type: 'font',
                value: 'Courier New, monospace',
                description: 'Font for code/data',
                category: 'fonts'
            },

            // ========== SIZES ==========
            {
                id: 'var-25',
                name: '--h1-size',
                type: 'size',
                value: '32px',
                description: 'H1 heading size',
                category: 'sizes'
            },
            {
                id: 'var-26',
                name: '--h2-size',
                type: 'size',
                value: '24px',
                description: 'H2 heading size',
                category: 'sizes'
            },
            {
                id: 'var-27',
                name: '--h3-size',
                type: 'size',
                value: '18px',
                description: 'H3 heading size',
                category: 'sizes'
            },
            {
                id: 'var-28',
                name: '--body-size',
                type: 'size',
                value: '14px',
                description: 'Body text size',
                category: 'sizes'
            },
            {
                id: 'var-29',
                name: '--small-size',
                type: 'size',
                value: '12px',
                description: 'Small text size',
                category: 'sizes'
            },
            {
                id: 'var-30',
                name: '--spacing-unit',
                type: 'size',
                value: '8px',
                description: 'Base spacing unit',
                category: 'sizes'
            }
        ]
    };
};

/**
 * Get variable by name
 * @param {Object} globalVariables - Global variables object
 * @param {string} name - Variable name (e.g., "--primary-color")
 * @returns {Object|null} Variable object or null
 */
export const getVariableByName = (globalVariables, name) => {
    if (!globalVariables?.variables) return null;
    return globalVariables.variables.find(v => v.name === name) || null;
};

/**
 * Get variables by category
 * @param {Object} globalVariables - Global variables object
 * @param {string} category - Category name (colors, fonts, sizes, custom)
 * @returns {Array} Array of variables in that category
 */
export const getVariablesByCategory = (globalVariables, category) => {
    if (!globalVariables?.variables) return [];
    return globalVariables.variables.filter(v => v.category === category);
};

/**
 * Get all variable categories
 * @param {Object} globalVariables - Global variables object
 * @returns {Array} Array of unique category names
 */
export const getVariableCategories = (globalVariables) => {
    if (!globalVariables?.variables) return [];
    const categories = new Set(globalVariables.variables.map(v => v.category));
    return Array.from(categories);
};

/**
 * Resolve a style value (Elementor-style)
 * Handles both old string format and new {type, value} object format
 * @param {string|Object} styleValue - Style value to resolve
 * @param {Object} globalVariables - Global variables object
 * @returns {string} Resolved CSS value
 */
export const resolveStyleValue = (styleValue, globalVariables) => {
    // Handle undefined/null
    if (!styleValue) return undefined;

    // Handle old format (string)
    if (typeof styleValue === 'string') {
        // If it's a variable reference like "var(--primary-color)", resolve it
        if (styleValue.startsWith('var(--')) {
            const varName = extractVariableName(styleValue);
            const variable = getVariableByName(globalVariables, varName);
            return variable?.value || styleValue;
        }
        // Otherwise return as-is (it's a direct color value)
        return styleValue;
    }

    // Handle new format (object with type and value)
    if (typeof styleValue === 'object' && styleValue !== null) {
        if (styleValue.type === 'global') {
            // Resolve global variable
            const variable = getVariableByName(globalVariables, styleValue.value);
            return variable?.value || '#000000';
        }

        if (styleValue.type === 'custom') {
            // Return custom value directly
            return styleValue.value;
        }
    }

    // Fallback
    return undefined;
};

/**
 * Convert old style format to new Elementor-style format
 * @param {Object} oldStyle - Old style object with string values
 * @returns {Object} New style object with {type, value} format
 */
export const migrateStyleFormat = (oldStyle) => {
    if (!oldStyle || typeof oldStyle !== 'object') return {};

    const newStyle = {};

    Object.entries(oldStyle).forEach(([key, value]) => {
        // Skip non-color properties (keep them as-is)
        const colorProperties = ['backgroundColor', 'textColor', 'borderColor', 'color'];

        if (!colorProperties.includes(key)) {
            newStyle[key] = value;
            return;
        }

        // Migrate color properties
        if (typeof value === 'string') {
            // Check if it's a variable reference
            if (value.startsWith('var(--')) {
                const varName = extractVariableName(value);
                newStyle[key] = { type: 'global', value: varName };
            } else {
                // It's a custom color
                newStyle[key] = { type: 'custom', value };
            }
        } else if (typeof value === 'object' && value !== null && value.type) {
            // Already in new format
            newStyle[key] = value;
        } else {
            // Unknown format, treat as custom
            newStyle[key] = { type: 'custom', value: value || '#000000' };
        }
    });

    return newStyle;
};

/**
 * Get the actual color value from a style value (for color picker display)
 * @param {string|Object} styleValue - Style value
 * @param {Object} globalVariables - Global variables object
 * @returns {string} Hex color value
 */
export const getColorValue = (styleValue, globalVariables) => {
    const resolved = resolveStyleValue(styleValue, globalVariables);
    return resolved || '#000000';
};

/**
 * Check if a style value is using a global variable
 * @param {string|Object} styleValue - Style value
 * @returns {boolean}
 */
export const isUsingGlobalVariable = (styleValue) => {
    if (typeof styleValue === 'object' && styleValue?.type === 'global') {
        return true;
    }
    if (typeof styleValue === 'string' && styleValue.startsWith('var(--')) {
        return true;
    }
    return false;
};
